import AARTextArea from "@/components/AARTextArea";
import AARTextField from "@/components/AARTextField";
import BaseDrawer from "@/components/BaseDrawer";
import Btn from "@/components/Btn";
import CustomLabel from "@/components/CustomLabel";
import { LookupValueMapping } from "@/constants/lookups";
import { useAllCompanies } from "@/hooks/useCompanies";
import { useAllLookups } from "@/hooks/useLookups";
import { useAppSelector } from "@/store/hooks/hooks";
import { hideLoader, showLoader } from "@/store/slices/loaderSlice";
import { RootState } from "@/store/store";
import { buildFilters, ExistingFilters } from "@/utils/kendo";
import { Button } from "@progress/kendo-react-buttons";
import {
  ComboBox,
  ComboBoxChangeEvent,
  MultiSelect,
  MultiSelectChangeEvent,
} from "@progress/kendo-react-dropdowns";
import {
  Checkbox,
  CheckboxChangeEvent,
  TextAreaChangeEvent,
  TextBoxChangeEvent,
} from "@progress/kendo-react-inputs";
import { Image as ImageIcon, Maximize, Upload, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import "react-image-crop/dist/ReactCrop.css";
import { useDispatch } from "react-redux";

interface IProps {
  showCreateDrawer: boolean;
  setShowCreateDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  addNewCompany: any;
  handleChange: (
    e:
      | TextBoxChangeEvent
      | ComboBoxChangeEvent
      | TextAreaChangeEvent
      | CheckboxChangeEvent
      | MultiSelectChangeEvent
  ) => void;
  onSave: () => void;
  isExisting: boolean;
  setIsExisting: React.Dispatch<React.SetStateAction<boolean>>;
}

function WriteDrawer({
  showCreateDrawer,
  setShowCreateDrawer,
  addNewCompany,
  handleChange,
  onSave,
  isExisting,
  setIsExisting,
  ...drawerProps
}: IProps) {
  const dispatch = useDispatch();

  const { selectedProject } = useAppSelector(
    (state: RootState) => state.projects
  );

  const { data: allCompaniesData, refetch } = useAllCompanies({
    filter: ExistingFilters("company", selectedProject?.id),
  });

  const [invalidFields, setInvalidFields] = useState<{
    [key: string]: boolean;
  }>({});
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(
    addNewCompany?.imageUrl || null
  );

  const { data: activitiesData } = useAllLookups(
    {
      filter: buildFilters(null, undefined, false, [
        { field: "type", operator: "eq", value: LookupValueMapping.Activities },
      ]),
      pageNumber: 0,
      pageSize: 99999
    },
    { enabled: true }
  );

  const [filteredActivities, setFilteredActivities] = useState(
    activitiesData?.data || []
  );
  const [activityFilterText, setActivityFilterText] = useState("");

  const handleActivityFilterChange = (event: any) => {
    const filterValue = event.filter?.value?.toLowerCase() || "";
    setActivityFilterText(filterValue);

    if (!filterValue) {
      setFilteredActivities(activitiesData?.data || []);
    } else {
      const filtered = (activitiesData?.data || []).filter((activity: any) =>
        activity.value.toLowerCase().includes(filterValue)
      );
      setFilteredActivities(filtered);
    }
  };

  useEffect(() => {
    if (activitiesData?.data) {
      setFilteredActivities(activitiesData.data);
    }
  }, [activitiesData]);

  const imgRef = useRef<HTMLImageElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleValidation = (name: string, isValid: boolean) => {
    setInvalidFields((prev: any) => ({
      ...prev,
      [name]: !isValid,
    }));
  };

  const validateSave = () => {
    return (
      Object.values(invalidFields).every((field) => !field) &&
      (imageFile || addNewCompany?.imageUrl)
    );
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (!file.type.startsWith("image/")) {
        alert("Please upload an image file");
        return;
      }

      const previewUrl = URL.createObjectURL(file);
      setImageFile(file);
      setImagePreview(previewUrl);

      if (handleChange) {
        const customEvent = {
          target: {
            name: "imageFile",
            value: file,
          },
        } as unknown as TextBoxChangeEvent;
        handleChange(customEvent);
      }
      // Reset input
      fileInputRef.current!.value = "";
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);

    if (handleChange) {
      const customEvent = {
        target: {
          name: "imageFile",
          value: null,
        },
      } as unknown as TextBoxChangeEvent;
      handleChange(customEvent);
    }
  };

  useEffect(() => {
    if (!showCreateDrawer) {
      setImageFile(null);
      setImagePreview(null);
      setInvalidFields({});
      setIsExisting(false);
    }
  }, [showCreateDrawer]);

  useEffect(() => {
    if (addNewCompany?.imageUrl) {
      setImagePreview(addNewCompany.imageUrl);
    }
  }, [addNewCompany?.imageUrl]);

  const getActivitiesValue = () => {
    const selectedValues = activitiesData?.data?.filter((item: any) =>
      addNewCompany?.activities?.includes(item.id)
    );
    return selectedValues;
  };

  const sortedCompanies = [...(allCompaniesData?.data || [])].sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  const getCompanyValue = () => {
    return sortedCompanies?.find((item: any) => item.id === addNewCompany?.id);
  };

  // Inside WriteDrawer component, add these new state variables
  const [filteredCompanies, setFilteredCompanies] = useState(sortedCompanies);
  const [filterText, setFilterText] = useState("");

  // Add a new handler for the ComboBox filtering
  const handleComboBoxFilterChange = (event: any) => {
    const filter = event.filter || "";
    setFilterText(filter);

    if (!filter) {
      // Reset to original list when filter is cleared
      setFilteredCompanies(sortedCompanies);
    } else {
      // Filter companies based on filter text
      const filtered = sortedCompanies.filter((company) => {
        return company.name
          .toLowerCase()
          .includes(filter?.value?.toLowerCase());
      });
      setFilteredCompanies(filtered);
    }
  };

  // Modify your existing handleChange function to reset filter when selection is made
  const originalHandleChange = handleChange;
  const enhancedHandleChange = (e: any) => {
    // Call the original handler
    originalHandleChange(e);

    // If this is from ComboBox and has a value (selection was made)
    if (e.target.name === "id" && e.target.value) {
      // Reset filter text and filtered list
      setFilterText("");
      setFilteredCompanies(sortedCompanies);
    }
  };

  const renderDrawerContent = () => (
    <div className="grid gap-4">
      {isExisting && (
        <div>
          <CustomLabel label="Select Company" required={true} />
          <ComboBox
            data={filteredCompanies}
            placeholder="Select Company"
            name="id"
            value={getCompanyValue() || ""}
            onChange={enhancedHandleChange}
            onFilterChange={handleComboBoxFilterChange}
            clearButton={false}
            filterable={true}
            textField="name"
            dataItemKey="id"
            suggest={true}
          />
        </div>
      )}

      <div>
        <AARTextField
          name="name"
          value={addNewCompany?.name}
          onChange={handleChange}
          onValidate={(name, isValid) =>
            setInvalidFields((prev) => ({
              ...prev,
              [name]: !isValid,
            }))
          }
          label="Company Name"
          required={true}
          placeholder="Enter Name"
          disabled={isExisting}
          regex={/[0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/}
          error="Invalid Name"
        />
      </div>
      <div>
        <AARTextArea
          name="location"
          value={addNewCompany?.location}
          onChange={handleChange}
          onValidate={(name, isValid) =>
            setInvalidFields((prev) => ({
              ...prev,
              [name]: !isValid,
            }))
          }
          label="Location"
          required={false}
          placeholder="Enter Location"
          disabled={isExisting}
          regex={/[0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/}
          error="Invalid Location"
        />
      </div>
      <div>
        <CustomLabel required label="Activities" />
        <MultiSelect
          data={filteredActivities}
          textField="value"
          dataItemKey="id"
          name="activities"
          disabled={isExisting}
          value={getActivitiesValue()}
          onChange={handleChange}
          onFilterChange={handleActivityFilterChange}
          placeholder="Select Activities"
          filterable={true}
        />
      </div>
      <div>
        <div className="grid grid-cols-2">
          <div className="flex items-center">
            <Checkbox
              name="isExternal"
              value={addNewCompany?.isExternal || false}
              onChange={(e) => {
                handleChange(e);
              }}
              label={"Is External"}
              disabled={isExisting}
            />
          </div>
          <div className="flex items-center">
            <Checkbox
              name="isMep"
              value={addNewCompany?.isMep || false}
              onChange={(e) => {
                handleChange(e);
              }}
              label={"Is MEP"}
              disabled={isExisting}
            />
          </div>
        </div>
      </div>
      <div>
        {!isExisting && (
          <div className="flex mb-2 pt-2 border-t border-neutral-30">
            <CustomLabel required label="Logo" />
            {(addNewCompany?.imageUrl || imageFile) && (
              <p
                className="text-primary underline ml-auto cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <span>+</span> Upload Image
              </p>
            )}
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="image/*"
              onChange={handleFileChange}
            />
          </div>
        )}
        {/* Image Preview Area */}
        <div className="w-full border border-slate-200 rounded-md bg-slate-50 overflow-hidden">
          {imagePreview ? (
            <div className="relative image-preview-bg">
              <img
                src={imagePreview}
                alt="Floor plan"
                className="w-full h-[250px] object-contain"
              />

              {!isExisting && (
                <div className="absolute top-2 right-2 flex gap-2">
                  <button
                    className="p-2 bg-white rounded-full shadow-md"
                    onClick={handleRemoveImage}
                    title="Remove Image"
                  >
                    <X className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div
              className="w-full h-[250px] flex flex-col items-center justify-center cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                <ImageIcon className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-500 mb-1">No image uploaded</p>
              <p className="text-primary font-medium text-sm">
                <Upload className="w-4 h-4 inline mr-1" />
                Click to upload
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <BaseDrawer
        isOpen={showCreateDrawer}
        onClose={() => setShowCreateDrawer(false)}
        title={
          isExisting
            ? "Add Exising Company"
            : addNewCompany?.id
            ? "Edit Company"
            : "Add New Company"
        }
        primaryActionText={isExisting ? "Save Exising Company" : "Save Company"}
        onPrimaryAction={onSave}
        primaryActionDisabled={!validateSave()}
        {...drawerProps}
        maxW="35vw"
      >
        {renderDrawerContent()}
      </BaseDrawer>
    </>
  );
}

export default WriteDrawer;
